import Select, { Props } from 'react-select';

import clsx from 'clsx';

import { Label, LABEL_TYPE, PARAGRAPH_ERROR_CLASSES } from '@/components/generic';
import { COLORS } from '@/enums';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { SelectOptionProps } from '@/types';

interface MultiSelectProps {
  id: string;
  label: string;
  options: SelectOptionProps[];
  className?: string;
  placeholder?: string;
  selectedValues: SelectOptionProps[];
  errorMessage?: string;
  isInvalid?: boolean;
  handleInputChange?: (inputValue: string) => void;
  textColor?: string;
  borderStyle?: string;
  fontWeight?: string;
  handleChange: (value: SelectOptionProps[]) => void;
  isRequired?: boolean;
  dropdownIndicatorColor?: string;
  controlShouldRenderValue?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  isSearchable?: boolean;
  showTags?: boolean;
  handleTagRemove?: (tag: SelectOptionProps) => void;
  tagIcon?: React.ElementType<{ className?: string }>;
  tagsHeight?: string;
}

export const MultiSelect = ({
  className,
  id,
  label,
  placeholder,
  options,
  selectedValues,
  errorMessage,
  isInvalid,
  textColor,
  borderStyle,
  fontWeight,
  handleChange,
  handleInputChange,
  isRequired,
  dropdownIndicatorColor,
  controlShouldRenderValue = true,
  fetchNextPage,
  hasNextPage,
  onBlur,
  disabled,
  isSearchable,
  showTags,
  handleTagRemove,
  tagIcon,
  tagsHeight,
  ...passDownProps
}: MultiSelectProps) => {
  const props: Props = {
    ...passDownProps
  };

  return (
    <div>
      {label && (
        <Label
          text={`${label}${isRequired ? ' *' : ''}`}
          type={LABEL_TYPE.PARAGRAPH2}
          className='mb-2.5'
        />
      )}
      <Select
        {...props}
        id={id}
        className={className}
        name={id}
        value={selectedValues}
        onChange={(selectedOptions) => handleChange(selectedOptions as SelectOptionProps[])}
        placeholder={placeholder}
        options={options}
        isDisabled={disabled}
        isMulti
        blurInputOnSelect={false}
        isSearchable={isSearchable}
        controlShouldRenderValue={controlShouldRenderValue}
        menuPlacement='auto'
        onInputChange={handleInputChange}
        isOptionDisabled={(option: unknown) => !!(option as SelectOptionProps).isDisabled}
        onMenuScrollToBottom={() => {
          if (hasNextPage) {
            fetchNextPage?.();
          }
        }}
        onBlur={onBlur}
        styles={{
          control: (base, state) => ({
            ...base,
            height: '40px',
            width: '100%',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: fontWeight ?? '400',
            color: textColor ?? base.color,
            fontSize: '14px',
            backgroundColor: COLORS.PRIMARY_WHITE,
            border: isInvalid
              ? `1px solid ${COLORS.ACCENT_RED}`
              : (borderStyle ?? state.isFocused)
                ? `1px solid ${COLORS.PRIMARY_BLUE}`
                : `1px solid ${COLORS.SECONDARY_BLUE_GRAY}`,
            boxShadow: 'none',
            '&:hover': {
              border: `1px solid ${COLORS.PRIMARY_BLUE}`
            }
          }),
          singleValue: (base) => ({
            ...base,
            color: COLORS.PRIMARY_BLACK,
            padding: '0 5px'
          }),
          input: (base) => ({
            ...base,
            color: COLORS.PRIMARY_BLACK,
            padding: '0 5px'
          }),
          indicatorSeparator: (base) => ({
            ...base,
            display: 'none'
          }),
          dropdownIndicator: (base, state) => ({
            ...base,
            color:
              (state.hasValue && COLORS.PRIMARY_BLACK) ||
              (dropdownIndicatorColor && dropdownIndicatorColor) ||
              COLORS.SECONDARY_GRAY,
            transition: 'all .2s ease',
            transform: state.isFocused && state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
            '&:hover': {
              color:
                (state.hasValue && COLORS.PRIMARY_BLACK) ||
                (dropdownIndicatorColor && dropdownIndicatorColor) ||
                COLORS.SECONDARY_GRAY
            }
          }),
          clearIndicator: (base) => ({
            ...base,
            display: 'none'
          }),
          placeholder: (base) => ({
            ...base,
            padding: '0 5px',
            color: textColor ?? COLORS.SECONDARY_GRAY,
            fontWeight: fontWeight ?? base.fontWeight
          }),
          menu: (base) => ({
            ...base,
            border: 'none',
            boxShadow: `0 0 8px 0 ${COLORS.PRIMARY_BLUE}`
          }),
          menuList: (base) => ({
            ...base,
            margin: '0 5px',
            cursor: 'pointer',
            '::-webkit-scrollbar': {
              width: '8px',
              height: '0px'
            },
            '::-webkit-scrollbar-track': {
              background: COLORS.PRIMARY_GRAY,
              margin: '16px'
            },
            '::-webkit-scrollbar-thumb': {
              background: COLORS.SECONDARY_STEEL_BLUE,
              borderRadius: '8px'
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: COLORS.PRIMARY_BLUE
            }
          }),
          option: (base, state) => ({
            ...base,
            cursor: 'pointer',
            backgroundColor: state.isSelected ? COLORS.PRIMARY_GRAY : COLORS.PRIMARY_WHITE,
            color: state.isSelected ? COLORS.PRIMARY_BLACK : COLORS.SECONDARY_GRAY,
            fontWeight: state.isSelected ? '600' : '400',
            fontSize: '14px',
            width: 'auto',
            height: 'fit-content',
            borderRadius: '5px',
            margin: (() => {
              const numberOfElements = state.selectProps?.options?.length || 0;
              return numberOfElements > 6 ? '5px 5px 5px 0' : '5px 0';
            })() as string,
            ':hover': {
              backgroundColor: COLORS.PRIMARY_GRAY,
              color: COLORS.PRIMARY_BLACK,
              fontWeight: state.isSelected ? '600' : '400'
            }
          })
        }}
      />
      {showTags && handleTagRemove && (
        <div
          className={clsx(
            'mt-2 flex w-full flex-wrap gap-1 overflow-y-auto',
            tagsHeight ?? 'max-h-14 2xl:max-h-20'
          )}>
          {selectedValues?.map((item) => (
            <AssetTagItem
              tag={item}
              handleTagRemove={handleTagRemove}
              key={item?.value as number}
              icon={tagIcon}
              className='!mx-0 !mr-1'
            />
          ))}
        </div>
      )}
      <div className='flex w-full justify-end'>
        <span className={`${PARAGRAPH_ERROR_CLASSES}`}>{isInvalid && errorMessage}</span>
      </div>
    </div>
  );
};

export default MultiSelect;
